/*
 * Products (U23).
 *
 * Lists the brand's products in a left picker and renders the selected product's
 * full configuration form (ProductConfigPayload) on the right. Operators+ can
 * create a new product or edit an existing one; viewers see the form read-only.
 * Saves go through `POST /products` (create-or-update); server 409/422 surface in
 * the form's Alert.
 */

import { useEffect, useState } from "react";
import { Alert, Button, EmptyState, PageHeader } from "../components/ui";
import { useAuth } from "../auth/AuthContext";
import { errorMessage } from "../api/errors";
import { useProducts, useUpsertProduct } from "../components/config/hooks";
import { ProductConfigForm } from "../components/config/ProductConfigForm";
import { fromDraft } from "../components/config/serialize";
import type { ProductConfigResponse } from "../components/config/hooks";
import styles from "../components/config/config.module.css";

const NEW = "__new__";

export function ProductsPage() {
  const { can } = useAuth();
  const canEdit = can("configure");
  const productsQuery = useProducts();
  const upsert = useUpsertProduct();
  const [selected, setSelected] = useState<string | null>(null);
  // A success banner shown after a save so the operator gets clear confirmation the
  // product was created/saved. It PERSISTS (no auto-expiry) so it can't be missed;
  // it clears only on the next New-product / picker action.
  const [justSaved, setJustSaved] = useState<{ key: string; created: boolean } | null>(null);

  useEffect(() => {
    if (!justSaved) return;
    // The config form can be scrolled down when Save is clicked; bring the banner
    // (at the top of the page) into view so the confirmation is always seen.
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [justSaved]);

  const products = productsQuery.data ?? [];
  // Default selection: first product once loaded (unless the user chose one).
  const activeKey =
    selected ?? (products.length > 0 ? products[0].product_key : null);

  const activeProduct: ProductConfigResponse | undefined =
    activeKey && activeKey !== NEW
      ? products.find((b) => b.product_key === activeKey)
      : undefined;

  if (productsQuery.isLoading) {
    return (
      <>
        <PageHeader title="Products" description={DESCRIPTION} />
        <p className={styles.note}>Loading products…</p>
      </>
    );
  }

  if (productsQuery.isError) {
    return (
      <>
        <PageHeader title="Products" description={DESCRIPTION} />
        <Alert tone="error">{errorMessage(productsQuery.error)}</Alert>
      </>
    );
  }

  const showingNew = activeKey === NEW;
  const noProducts = products.length === 0;

  return (
    <>
      <PageHeader
        title="Products"
        description={DESCRIPTION}
        actions={
          canEdit ? (
            <Button
              variant="primary"
              onClick={() => {
                upsert.reset();
                setJustSaved(null);
                setSelected(NEW);
              }}
            >
              New product
            </Button>
          ) : undefined
        }
      />

      {justSaved && (
        <Alert tone="success">
          {justSaved.created
            ? `Product “${justSaved.key}” created.`
            : `Product “${justSaved.key}” saved.`}
        </Alert>
      )}

      {noProducts && !showingNew ? (
        <EmptyState
          icon="◈"
          title="No products configured"
          description={
            canEdit
              ? "Create your first product to configure its identity, master sheet, AD_NAMING column, and scheduled conversion settings."
              : "No products have been configured for this brand yet."
          }
          action={
            canEdit ? (
              <Button variant="primary" onClick={() => setSelected(NEW)}>
                New product
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className={styles.layout}>
          <nav className={styles.picker} aria-label="Products">
            <div className={styles.pickerHeading}>Products</div>
            {products.map((b) => (
              <button
                key={b.product_key}
                type="button"
                className={`${styles.pickerItem} ${
                  activeKey === b.product_key ? styles.pickerItemActive : ""
                }`}
                onClick={() => {
                  upsert.reset();
                  setJustSaved(null);
                  setSelected(b.product_key);
                }}
              >
                <span className={styles.pickerItemKey}>{b.product_key}</span>
                {b.label && (
                  <span className={styles.pickerItemLabel}>{b.label}</span>
                )}
              </button>
            ))}
            {showingNew && (
              <button
                type="button"
                className={`${styles.pickerItem} ${styles.pickerItemActive}`}
              >
                <span className={styles.pickerItemKey}>New product</span>
                <span className={styles.pickerItemLabel}>unsaved</span>
              </button>
            )}
          </nav>

          <ProductConfigForm
            key={showingNew ? NEW : activeProduct?.product_key ?? "none"}
            product={showingNew ? undefined : activeProduct}
            productKey={showingNew ? "" : activeProduct?.product_key ?? ""}
            canEdit={canEdit}
            saving={upsert.isPending}
            saveError={upsert.error}
            onSave={({ key, config }) => {
              const created = showingNew;
              // CREATE → POST (no originalKey); EDIT → PUT /products/{originalKey},
              // where a changed `key` renames the product.
              const originalKey = showingNew ? null : activeProduct?.product_key ?? null;
              upsert.mutate(
                { originalKey, body: { key, config: fromDraft(config) } },
                {
                  onSuccess: (saved) => {
                    setSelected(saved.product_key);
                    setJustSaved({ key: saved.product_key, created });
                  },
                },
              );
            }}
          />
        </div>
      )}
    </>
  );
}

const DESCRIPTION =
  "Configure each product's identity, master sheet, AD_NAMING column, and scheduled conversion settings.";

export default ProductsPage;
